import React, { useState } from 'react';
import { X, Upload, Image as ImageIcon, Star, Loader2 } from 'lucide-react';
import axios from 'axios';

function ImageUploadModal({ productId, productTitle, onClose, onUploadSuccess }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [primaryIndex, setPrimaryIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };

  const addFiles = (files) => {
    const validFiles = files.filter(file => {
      const isValid = file.type.startsWith('image/');
      if (!isValid) {
        alert(`${file.name} no es una imagen válida`);
      }
      return isValid;
    });

    if (validFiles.length === 0) return;

    setSelectedFiles(prev => [...prev, ...validFiles]);

    // Create preview URLs
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrls(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
    
    // Adjust primary index if needed
    if (primaryIndex === index) {
      setPrimaryIndex(0);
    } else if (primaryIndex > index) {
      setPrimaryIndex(prev => prev - 1);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Por favor selecciona al menos una imagen');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('is_primary', 'true');
      formData.append('primary_image_index', primaryIndex.toString());

      const { data } = await axios.post(
        `${baseUrl}/api/products/${productId}/images/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      onUploadSuccess(data);
      onClose();
    } catch (error) {
      console.error('Error uploading images:', error);
      alert(error.response?.data?.error || 'Error al subir las imágenes');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 font-montserrat">
              Subir Imágenes
            </h2>
            <p className="text-sm text-slate-600 font-montserrat mt-1">
              {productTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={uploading}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Upload Area */}
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              dragActive
                ? 'border-brand-primary bg-brand-primary/5'
                : 'border-slate-300 hover:border-brand-primary/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
              Arrastra imágenes aquí
            </h3>
            <p className="text-sm text-slate-600 font-montserrat mb-4">
              o haz clic para seleccionar archivos
            </p>
            <label className="inline-block">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                disabled={uploading}
              />
              <span className="px-6 py-2.5 bg-brand-primary text-white rounded-lg font-semibold font-montserrat cursor-pointer hover:bg-brand-primary/90 transition-colors inline-block">
                Seleccionar Imágenes
              </span>
            </label>
            <p className="text-xs text-slate-500 font-montserrat mt-4">
              PNG, JPG, GIF, WEBP hasta 10MB por imagen
            </p>
          </div>

          {/* Preview Grid */}
          {previewUrls.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-slate-900 font-montserrat mb-3">
                Imágenes Seleccionadas ({previewUrls.length})
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {previewUrls.map((url, index) => (
                  <div
                    key={index}
                    className="relative group aspect-square rounded-lg overflow-hidden border-2 transition-all"
                    style={{
                      borderColor: primaryIndex === index ? '#B1336E' : '#e2e8f0'
                    }}
                  >
                    <img
                      src={url}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Primary Badge */}
                    {primaryIndex === index && (
                      <div className="absolute top-2 left-2 bg-brand-primary text-white px-2 py-1 rounded-md text-xs font-bold font-montserrat flex items-center gap-1">
                        <Star className="w-3 h-3 fill-current" />
                        Principal
                      </div>
                    )}

                    {/* Overlay Controls */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        onClick={() => setPrimaryIndex(index)}
                        disabled={uploading}
                        className="p-2 bg-white text-brand-primary rounded-lg hover:bg-brand-primary hover:text-white transition-colors disabled:opacity-50"
                        title="Marcar como principal"
                      >
                        <Star className={`w-4 h-4 ${primaryIndex === index ? 'fill-current' : ''}`} />
                      </button>
                      <button
                        onClick={() => removeFile(index)}
                        disabled={uploading}
                        className="p-2 bg-white text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-colors disabled:opacity-50"
                        title="Eliminar"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Image Number */}
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-montserrat">
                      {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-6 py-4 flex items-center justify-between">
          <p className="text-sm text-slate-600 font-montserrat">
            {previewUrls.length > 0 && (
              <>
                <Star className="w-4 h-4 inline text-brand-primary" /> Imagen {primaryIndex + 1} será la principal
              </>
            )}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={uploading}
              className="px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg font-semibold font-montserrat hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleUpload}
              disabled={uploading || selectedFiles.length === 0}
              className="px-6 py-2.5 bg-brand-primary text-white rounded-lg font-semibold font-montserrat hover:bg-brand-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Subiendo...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Subir {selectedFiles.length} {selectedFiles.length === 1 ? 'Imagen' : 'Imágenes'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageUploadModal;